import {
  CheckSquare,
  CircleDot,
  Gauge,
  Grid3X3,
  GripHorizontal,
  ListOrdered,
  LucideIcon,
  MessageSquare,
  Shuffle,
  SlidersHorizontal,
  Star,
  Tag,
  Trash2,
} from "lucide-react-native";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Animated, LayoutChangeEvent, Platform, View } from "react-native";

import { questionHasError } from "@/components/studies/completeness";
import {
  Button,
  ButtonText,
  Card,
  DeleteDialog,
  HStack,
  Media,
  MediaGallery,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import {
  QuestionDraft,
  Question as QuestionType,
  VariableDef,
} from "@/types/resources";
import { QuestionType as QuestionTypeEnum } from "@/types/utils";
import {
  CategoryAssignmentQuestionEditor,
  LinearScaleQuestionEditor,
  MultipleChoiceQuestionEditor,
  MultiSelectMatrixQuestionEditor,
  NpsQuestionEditor,
  OpenEndedQuestionEditor,
  PricingQuestionEditor,
  RankingQuestionEditor,
  RatingScaleQuestionEditor,
  SingleChoiceQuestionEditor,
  SingleSelectMatrixQuestionEditor,
} from "./editors";
import { QuestionVariablesProvider } from "./editors/QuestionVariablesContext";
import {
  renderVariableText,
  variableTypeMap,
} from "./editors/variableHighlight";
import QuestionContent from "./QuestionContent";
import { SkipRules } from "./rules";

interface QuestionProps {
  allQuestions?: QuestionType[];
  animatable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onDeleted?: () => void;
  onPress?: () => void;
  onUpdated?: (question: QuestionType) => void;
  question: QuestionType;
  studyId: string;
  /** Variables of the parent section, injected into the question's text. */
  variables?: VariableDef[];
}

const getQuestionIcon = (type: string): LucideIcon => {
  switch (type) {
    case "linear_scale":
      return SlidersHorizontal;
    case "multi_select_matrix":
      return Grid3X3;
    case "multiple_choice":
      return CheckSquare;
    case "nps":
      return Gauge;
    case "open_ended":
      return MessageSquare;
    case "pricing":
      return Tag;
    case "rating_scale":
      return Star;
    case "category_assignment":
      return Shuffle;
    case "ranking":
      return ListOrdered;
    case "single_choice":
      return CircleDot;
    case "single_select_matrix":
      return GripHorizontal;
    default:
      return CircleDot;
  }
};

export interface QuestionRef {
  collapse: () => void;
  expand: () => void;
}

const Question = forwardRef<QuestionRef, QuestionProps>(
  (
    {
      allQuestions = [],
      animatable = false,
      disabled = false,
      loading = false,
      onDeleted,
      onPress,
      onUpdated,
      question,
      studyId,
      variables = [],
    },
    ref
  ) => {
    const { i18n } = useLocalization();
    const [galleryIndex, setGalleryIndex] = useState(0);
    const [galleryVisible, setGalleryVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [questionDraft, setQuestionDraft] = useState<
      QuestionDraft<QuestionTypeEnum>
    >({
      assignment: question.assignment ?? null,
      carry_forward: question.carry_forward ?? null,
      lines: question.lines || [],
      media: question.media_urls || [],
      options: question.options || [],
      rules:
        (question && "rules" in question ? question.rules : undefined) || [],
      text: question.text || "",
      type: question.type || "open_ended",
    });
    const [updatedQuestion, setUpdatedQuestion] = useState<QuestionType | null>(
      null
    );
    const [useImages, setUseImages] = useState(
      (question.options.length || 0) === 0
    );

    // Animation state
    const [measuredHeight, setMeasuredHeight] = useState(0);
    const cardHeight = useRef(new Animated.Value(0)).current;
    const cardOpacity = useRef(new Animated.Value(1)).current;
    const [isCollapsed, setIsCollapsed] = useState(false);
    const isAnimating = useRef(false);
    const animationTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleLayout = (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      if (height > 0 && !isCollapsed && !isAnimating.current) {
        setMeasuredHeight(height);
        cardHeight.setValue(height);
      }
    };

    useImperativeHandle(ref, () => ({
      collapse: () => {
        if (
          !animatable ||
          isCollapsed ||
          isAnimating.current ||
          measuredHeight === 0
        )
          return;

        isAnimating.current = true;
        setIsCollapsed(true);

        Animated.parallel([
          Animated.timing(cardHeight, {
            toValue: 0,
            duration: 300,
            useNativeDriver: false,
          }),
          Animated.timing(cardOpacity, {
            toValue: 0,
            duration: 300,
            useNativeDriver: false,
          }),
        ]).start(() => {
          if (animationTimeout.current) {
            clearTimeout(animationTimeout.current);
          }
          animationTimeout.current = setTimeout(() => {
            isAnimating.current = false;
          }, 500);
        });
      },
      expand: () => {
        if (
          !animatable ||
          !isCollapsed ||
          isAnimating.current ||
          measuredHeight === 0
        )
          return;

        isAnimating.current = true;
        setIsCollapsed(false);

        Animated.parallel([
          Animated.timing(cardHeight, {
            toValue: measuredHeight,
            duration: 300,
            useNativeDriver: false,
          }),
          Animated.timing(cardOpacity, {
            toValue: 1,
            duration: 300,
            useNativeDriver: false,
          }),
        ]).start(() => {
          if (animationTimeout.current) {
            clearTimeout(animationTimeout.current);
          }
          animationTimeout.current = setTimeout(() => {
            isAnimating.current = false;
          }, 500);
        });
      },
    }));

    const mediaMapping = useMemo(
      () =>
        question.media_urls.reduce(
          (acc, url, index) => {
            acc[url] = question.media[index];
            return acc;
          },
          {} as Record<string, string>
        ) || {},
      [question]
    );

    const {
      error: updateError,
      loading: updateLoading,
      updateQuestion,
      uploadProgress,
    } = API.Studies.useUpdateQuestion(studyId);

    const {
      deleteQuestion,
      error: deleteError,
      loading: deleteLoading,
    } = API.Studies.useDeleteQuestion(studyId, question.id);

    const handleCardClick = () => {
      if (!disabled && !isEditMode && studyId) {
        setIsEditMode(true);
        onPress?.();
      }
    };

    const handleDeleteClick = () => setShowDeleteDialog(true);
    const handleDeleteCancel = () => setShowDeleteDialog(false);

    const handleDeleteConfirm = async () => {
      const success = await deleteQuestion();
      if (success) {
        setShowDeleteDialog(false);
        onDeleted?.();
      }
    };

    const resetDraft = useCallback(() => {
      if (!question) return;
      setQuestionDraft({
        assignment: question.assignment ?? null,
        carry_forward: question.carry_forward ?? null,
        lines: question.lines,
        media: question.media_urls,
        options: question.options,
        rules: "rules" in question ? question.rules : [],
        text: question.text,
        type: question.type,
      });
      setUseImages(question.options.length === 0);
    }, [question]);

    const handleCancel = () => {
      setIsEditMode(false);
      resetDraft();
    };

    const handleSave = async () => {
      const result = await updateQuestion(question.id, {
        assignment: questionDraft.assignment ?? null,
        carry_forward: questionDraft.carry_forward ?? null,
        lines: questionDraft.lines,
        media: questionDraft.media
          .map((media) =>
            typeof media === "string" ? mediaMapping[media] : media
          )
          .filter(Boolean),
        options: questionDraft.options,
        order: question.order,
        rules: (questionDraft.rules || []).map((rule) => ({
          id: rule.id || null,
          operator: rule.operator,
          option_index: rule.option_index,
          probability: rule.probability ?? null,
          source_question_id: rule.source_question_id ?? null,
          target_question_id: rule.target_question_id ?? null,
        })),
        section_id: question.section,
        text: questionDraft.text,
      });
      if (result) {
        setUpdatedQuestion(result);
      }
    };

    const validateRules = useCallback(() => {
      // Get probability-based default rules (rules with null option_index, null source_question_id, and non-null probability)
      const probabilityBasedRules = questionDraft.rules?.filter(
        (r) =>
          r.option_index === null &&
          r.source_question_id === null &&
          r.probability !== null
      );

      // Calculate sum of probabilities
      const probabilitySum =
        (probabilityBasedRules?.reduce(
          (sum, rule) => sum + (rule.probability || 0),
          0
        ) || 0) * 100;

      // Validation: sum must be less than or equal to 100%
      return probabilitySum <= 100;
    }, [questionDraft.rules]);

    const isSaveDisabled = useCallback(() => {
      const MIN_OPTIONS = 2;
      const MIN_OPTIONS_SCALE = 3;
      const MIN_LINES = 2;

      // Check rules validation
      if (!validateRules()) {
        return true;
      }

      switch (questionDraft.type) {
        case "category_assignment": {
          // Missing track destinations don't block save (authors may add the
          // downstream questions first); the question is flagged in red via
          // `questionHasError` so they come back to it before launch.
          return (
            questionDraft.options.length < MIN_OPTIONS ||
            questionDraft.options.some((opt) => !opt.trim())
          );
        }
        case "single_choice":
        case "multiple_choice": {
          // Carried options are ported at run time, so don't require a list.
          if (questionDraft.carry_forward?.target === "options")
            return !questionDraft.text?.trim();
          return (
            !questionDraft.text?.trim() ||
            (useImages
              ? questionDraft.media.length < MIN_OPTIONS
              : questionDraft.options.length < MIN_OPTIONS ||
                questionDraft.options.some((opt) => !opt.trim()))
          );
        }
        case "ranking":
          return (
            !questionDraft.text?.trim() ||
            (questionDraft.lines?.length ?? 0) < MIN_LINES ||
            (questionDraft.lines?.some((line) => !line.trim()) ?? true)
          );
        case "single_select_matrix":
        case "multi_select_matrix": {
          // A matrix can carry either dimension; the carried one is ported at
          // run time, so only the manually-authored dimension is required.
          const target = questionDraft.carry_forward?.target;
          const optionsInvalid =
            target !== "options" &&
            (questionDraft.options.length < MIN_OPTIONS ||
              questionDraft.options.some((opt) => !opt.trim()));
          const linesInvalid =
            target !== "lines" &&
            ((questionDraft.lines?.length ?? 0) < MIN_LINES ||
              (questionDraft.lines?.some((line) => !line.trim()) ?? true));
          return !questionDraft.text?.trim() || optionsInvalid || linesInvalid;
        }
        case "linear_scale":
          return (
            !questionDraft.text?.trim() ||
            questionDraft.options.length < MIN_OPTIONS_SCALE ||
            !questionDraft.options[0]?.trim() ||
            !questionDraft.options[questionDraft.options.length - 1]?.trim()
          );
        case "rating_scale":
          return (
            !questionDraft.text?.trim() ||
            questionDraft.options.length < MIN_OPTIONS_SCALE
          );
        case "nps":
        case "open_ended":
        case "pricing":
          return !questionDraft.text?.trim();
        default:
          return true;
      }
    }, [questionDraft, useImages, validateRules]);

    const hasUnsavedChanges = useCallback(() => {
      if (!isEditMode || !question) return false;
      const originalRules = "rules" in question ? question.rules : [];
      return (
        questionDraft.text !== question.text ||
        JSON.stringify(questionDraft.options) !==
          JSON.stringify(question.options) ||
        JSON.stringify(questionDraft.media) !==
          JSON.stringify(question.media_urls) ||
        JSON.stringify(questionDraft.assignment ?? null) !==
          JSON.stringify(question.assignment ?? null) ||
        JSON.stringify(questionDraft.carry_forward ?? null) !==
          JSON.stringify(question.carry_forward ?? null) ||
        JSON.stringify(questionDraft.rules ?? []) !==
          JSON.stringify(originalRules)
      );
    }, [isEditMode, question, questionDraft]);

    // Submit on Enter key when in edit mode
    useEffect(() => {
      if (Platform.OS !== "web" || !isEditMode) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (
          e.key === "Enter" &&
          !e.shiftKey &&
          !isSaveDisabled() &&
          !updateLoading
        ) {
          e.preventDefault();
          handleSave();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    });

    // Warn on page leave with unsaved changes
    useEffect(() => {
      if (Platform.OS !== "web") return;

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges()) {
          e.preventDefault();
          return "";
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
      if (updatedQuestion && onUpdated) {
        onUpdated(updatedQuestion);
        setIsEditMode(false);
      }
    }, [updatedQuestion, onUpdated]);

    useEffect(() => {
      resetDraft();
    }, [resetDraft]);

    const questionOrder =
      question && "order" in question ? question.order : null;
    const rules = question && "rules" in question ? question.rules : undefined;

    const renderQuestionDisplay = () => (
      <VStack className="gap-3">
        <Skeleton className="h-5 w-full rounded" isLoaded={!loading}>
          <Text
            className={`text-sm text-typography-900 leading-relaxed ${disabled ? "" : "pr-8"}`}
          >
            {questionOrder && (
              <Text className="font-semibold">{questionOrder}. </Text>
            )}
            {question.type === "category_assignment"
              ? i18n.t("questions.categoryAssignment.explanation")
              : renderVariableText(question.text, variableTypes)}
          </Text>
        </Skeleton>

        {/* Media sits directly under the question text so the visual stimulus
            stays anchored to the prompt it illustrates — placing it after
            the answer options would visually detach the image from what it
            refers to. */}
        {(question.media_urls.length || 0) > 0 && (
          <>
            <HStack className="gap-2 flex-wrap">
              {question!.media_urls.map((url, index) => (
                <Media
                  key={index}
                  alt={`Media ${index + 1}`}
                  onPress={() => {
                    setGalleryIndex(index);
                    setGalleryVisible(true);
                  }}
                  source={url}
                />
              ))}
            </HStack>
            <MediaGallery
              initialIndex={galleryIndex}
              media={question!.media_urls}
              onClose={() => setGalleryVisible(false)}
              visible={galleryVisible}
            />
          </>
        )}

        {loading ? (
          <VStack className="gap-2 pl-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full rounded" />
            ))}
          </VStack>
        ) : (
          question && (
            <QuestionContent allQuestions={allQuestions} question={question} />
          )
        )}

        {rules && <SkipRules allQuestions={allQuestions} rules={rules} />}
      </VStack>
    );

    /* Section variables plus the question's own (mutually exclusive today, but
       merged defensively) feed `{{placeholder}}` highlighting/autocomplete. */
    const editorVariables = useMemo<VariableDef[]>(() => {
      const own = question.config?.variables ?? [];
      const byName = new Map<string, VariableDef>();
      [...variables, ...own].forEach((v) => byName.set(v.name, v));
      return [...byName.values()];
    }, [question.config?.variables, variables]);

    const variableTypes = useMemo(
      () => variableTypeMap(editorVariables),
      [editorVariables]
    );

    /* A saved question is "in error" when it's missing the content it needs to
       run — drives the red "Add options/rows" hint and the red left rail. */
    const hasError = useMemo(
      () =>
        question
          ? questionHasError(question as QuestionType, allQuestions)
          : false,
      [question, allQuestions]
    );

    const renderQuestionEditor = () => {
      switch (questionDraft.type) {
        case "category_assignment":
          return (
            <CategoryAssignmentQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"category_assignment">}
            />
          );
        case "single_choice":
          return (
            <SingleChoiceQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"single_choice">}
              setUseImages={setUseImages}
              useImages={useImages}
            />
          );
        case "multiple_choice":
          return (
            <MultipleChoiceQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"multiple_choice">}
              setUseImages={setUseImages}
              useImages={useImages}
            />
          );
        case "open_ended":
          return (
            <OpenEndedQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"open_ended">}
              questionOrder={questionOrder ?? undefined}
            />
          );
        case "nps":
          return (
            <NpsQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"nps">}
              questionOrder={questionOrder ?? undefined}
            />
          );
        case "pricing":
          return (
            <PricingQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"pricing">}
              questionOrder={questionOrder ?? undefined}
            />
          );
        case "rating_scale":
          return (
            <RatingScaleQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"rating_scale">}
            />
          );
        case "linear_scale":
          return (
            <LinearScaleQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"linear_scale">}
            />
          );
        case "ranking":
          return (
            <RankingQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"ranking">}
            />
          );
        case "single_select_matrix":
          return (
            <SingleSelectMatrixQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"single_select_matrix">}
            />
          );
        case "multi_select_matrix":
          return (
            <MultiSelectMatrixQuestionEditor
              allQuestions={allQuestions}
              currentQuestionOrder={questionOrder ?? undefined}
              onChange={setQuestionDraft}
              question={questionDraft as QuestionDraft<"multi_select_matrix">}
            />
          );
        default:
          return (
            <Text className="text-typography-600">
              {i18n.t("questions.editorComingSoon")}
            </Text>
          );
      }
    };

    const cardContent = (
      <>
        {isEditMode ? (
          /* Own stacking context above the footer so the variable-suggestion
             dropdown (absolutely positioned inside the editor) paints over the
             footer divider/buttons instead of being covered by them. */
          <VStack className="relative z-10">
            <QuestionVariablesProvider value={editorVariables}>
              {renderQuestionEditor()}
            </QuestionVariablesProvider>
          </VStack>
        ) : (
          renderQuestionDisplay()
        )}

        {isEditMode && (
          <HStack className="relative z-0 justify-between items-center pt-3 mt-3 border-t border-outline-100">
            <Pressable
              className="p-2 rounded-lg hover:bg-error-50"
              onPress={handleDeleteClick}
            >
              <Trash2 color="rgb(var(--color-error-600))" size={20} />
            </Pressable>
            <HStack className="gap-2">
              <Button
                isDisabled={updateLoading}
                onPress={handleCancel}
                size="sm"
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button
                className="bg-primary-500"
                isDisabled={isSaveDisabled() || updateLoading}
                onPress={handleSave}
                size="sm"
              >
                <ButtonText className="text-white">
                  {uploadProgress > 0
                    ? i18n.t("misc.uploadingMedia", {
                        count: questionDraft.media.length,
                        percent: i18n.numberToPercentage(uploadProgress * 100, {
                          precision: 0,
                        }),
                      })
                    : updateLoading
                      ? i18n.t("misc.saving")
                      : i18n.t("misc.save")}
                </ButtonText>
              </Button>
            </HStack>
          </HStack>
        )}

        {updateError && (
          <Text className="text-error-600 text-xs mt-2 text-right">
            {updateError}
          </Text>
        )}

        <DeleteDialog
          body={
            question &&
            (("targeted_by" in question && question.targeted_by.length > 0) ||
              ("checked_by" in question && question.checked_by.length > 0))
              ? i18n.t("questions.confirmDeleteWithRulesMessage", {
                  count:
                    ("targeted_by" in question
                      ? question.targeted_by.length
                      : 0) +
                    ("checked_by" in question ? question.checked_by.length : 0),
                })
              : i18n.t("questions.confirmDeleteMessage")
          }
          error={deleteError}
          isOpen={showDeleteDialog}
          loading={deleteLoading}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          title={i18n.t("questions.confirmDelete")}
        />
      </>
    );

    const TypeIcon = getQuestionIcon(question.type);
    const cardBody = (
      <HStack className="items-stretch">
        <View
          className={`items-center justify-start pt-4 rounded-l-md ${
            hasError ? "bg-error-700" : "bg-typography-400"
          }`}
          style={{ width: 28 }}
        >
          <TypeIcon color="rgb(var(--color-typography-0))" size={12} />
        </View>
        <VStack className="flex-1 p-4">{cardContent}</VStack>
      </HStack>
    );

    const cardClassName = `p-0 border rounded-xl overflow-hidden ${
      hasError ? "border-error-700" : "border-outline-200"
    }`;
    const card =
      isEditMode || disabled ? (
        <Card className={cardClassName}>{cardBody}</Card>
      ) : (
        <Pressable onPress={handleCardClick}>
          <Card className={cardClassName}>{cardBody}</Card>
        </Pressable>
      );

    if (animatable) {
      return (
        <Animated.View
          style={
            measuredHeight > 0
              ? {
                  height: cardHeight,
                  opacity: cardOpacity,
                  overflow: "hidden",
                }
              : undefined
          }
        >
          <View onLayout={handleLayout}>{card}</View>
        </Animated.View>
      );
    }

    return card;
  }
);

Question.displayName = "Question";

export default Question;
